import { Injectable, DOCUMENT, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { TenantBrandingService } from './tenant-branding.service';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private titleService = inject(Title);
  private meta = inject(Meta);
  private document = inject<Document>(DOCUMENT);
  private tenantBrandingService = inject(TenantBrandingService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  async initializeAppMeta(): Promise<void> {
    await this.tenantBrandingService.loadBranding();
    const branding = this.tenantBrandingService.branding;
    console.log('Initializing app metadata...');
    this.updateTitle(branding.appTitle);
    this.updateDescription(branding.description);
    this.updateKeywords(branding.keywords);
    this.updateFavicon(branding.favicon);
    this.updateLogo(branding.portalLogo);
    this.updateOGTags(
      branding.appTitle,
      branding.description,
      branding.portalLogo,
      branding.circleWebsite
    );
  }

  updateTitle(title: string) {
    console.log(`Setting title to: ${title}`);
    this.titleService.setTitle(title);
  }

  updateDescription(description: string) {
    const metaTag = this.meta.getTag("name='description'");
    if (!metaTag) {
      console.log(`adding description to: ${description}`);
      // Create the description tag if it doesn't exist
      this.meta.addTag({ name: 'description', content: description });
    } else {
      console.log(`Setting description to: ${description}`);
      // Update the existing tag
      this.meta.updateTag({ name: 'description', content: description });
    }
  }

  updateOGTags(
    title: string,
    description: string,
    imageUrl: string,
    pageUrl: string
  ) {
    console.log('Updating Open Graph meta tags...');
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
  }

  updateKeywords(keywords: string) {
    const metaTag = this.meta.getTag("name='keywords'");
    if (!metaTag) {
      console.log(`adding keywords to: ${keywords}`);
      // Create the keywords tag if it doesn't exist
      this.meta.addTag({ name: 'keywords', content: keywords });
    } else {
      console.log(`Setting keywords to: ${keywords}`);
      // Update the existing tag
      this.meta.updateTag({ name: 'keywords', content: keywords });
    }
  }

  updateFavicon(faviconPath: string) {
    console.log(`Setting favicon to: ${faviconPath}`);
    let link: HTMLLinkElement | null =
      this.document.querySelector("link[rel*='icon']");
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'shortcut icon';
      this.document.head.appendChild(link);
    }
    link.href = faviconPath;
  }

  updateLogo(logoPath: string) {
    console.log(`Setting logo to: ${logoPath}`);
    const logoElement = this.document.querySelector(
      'fuse-splash-screen img'
    ) as HTMLImageElement;
    if (logoElement) {
      logoElement.src = logoPath;
    }
  }
}
